/**
 * グラフの座標計算（components.md §3.3 DurationChart）。DOM に触れない純関数。
 * SVG は viewBox で拡大縮小せず、コンテナの実寸で描く。ここではその実寸からの座標を出す。
 */
import { groupDigits } from '../../lib/complexity';

export interface Margin {
  l: number;
  r: number;
  t: number;
  b: number;
}

export interface ChartLayout {
  width: number;
  height: number;
  margin: Margin;
  /** 幅 520px 未満（x 目盛りを 3 本、制限線のラベルを短縮）。 */
  narrow: boolean;
}

export const CHART_HEIGHT = 280;
export const MIN_CHART_WIDTH = 280;
export const NARROW_WIDTH = 520;

/** コンテナ幅から実寸のレイアウトを決める。 */
export function chartLayout(containerWidth: number, height = CHART_HEIGHT): ChartLayout {
  const width = Math.max(MIN_CHART_WIDTH, Math.round(containerWidth));
  const narrow = width < NARROW_WIDTH;
  return {
    width,
    height,
    narrow,
    margin: { l: narrow ? 56 : 72, r: narrow ? 52 : 84, t: 24, b: 40 },
  };
}

/** [d0, d1] → [r0, r1] の線形写像。d0 === d1 のときは r0 に置く。 */
export function linearScale(d0: number, d1: number, r0: number, r1: number): (v: number) => number {
  const span = d1 - d0;
  return (v) => (span === 0 ? r0 : r0 + ((v - d0) / span) * (r1 - r0));
}

/** 1, 2, 5 × 10^k のうち、raw 以上で最小のもの。 */
export function niceStep(raw: number): number {
  if (!(raw > 0) || !Number.isFinite(raw)) return 1;
  const k = Math.floor(Math.log10(raw));
  const base = 10 ** k;
  for (const m of [1, 2, 5, 10]) {
    if (m * base >= raw * (1 - 1e-12)) return m * base;
  }
  return 10 * base;
}

/**
 * 0 から max を覆う目盛り（0 を含む）。本数はおよそ count + 1。
 * 最後の目盛りは max 以上になる（軸の上端をそこに合わせる）。
 */
export function niceTicks(max: number, count: number): number[] {
  if (!(max > 0) || !Number.isFinite(max)) return [0, 1];
  const step = niceStep(max / Math.max(1, count));
  const out: number[] = [];
  const n = Math.ceil(max / step - 1e-9);
  for (let i = 0; i <= n; i++) out.push(i * step);
  return out;
}

/** N 軸の目盛りの文字（0, 500, 50K, 1.5M）。 */
export function formatAxisN(n: number): string {
  if (n === 0) return '0';
  if (n >= 1e6) return `${trim(n / 1e6)}M`;
  if (n >= 1e3) return `${trim(n / 1e3)}K`;
  return groupDigits(n);
}

function trim(x: number): string {
  return String(Math.round(x * 10) / 10);
}

/** 画面上の x に最も近い点の添字。xs が空なら -1。 */
export function nearestIndex(xs: readonly number[], px: number): number {
  let best = -1;
  let dist = Infinity;
  xs.forEach((x, i) => {
    const d = Math.abs(x - px);
    if (d < dist) {
      best = i;
      dist = d;
    }
  });
  return best;
}

/** 折れ線の d 属性。点が無ければ空文字。 */
export function linePath(pts: readonly { x: number; y: number }[]): string {
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${round2(p.x)},${round2(p.y)}`).join('');
}

/** ✕ 印の d 属性（中心 cx, cy、半径 k）。 */
export function crossPath(cx: number, cy: number, k = 4.5): string {
  const a = round2(cx - k);
  const b = round2(cx + k);
  const c = round2(cy - k);
  const d = round2(cy + k);
  return `M${a},${c}L${b},${d}M${b},${c}L${a},${d}`;
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

/** ツールチップの左端。右にはみ出すなら点の左に置く。 */
export function tooltipLeft(px: number, tipWidth: number, chartWidth: number, gap = 12): number {
  let left = px + gap;
  if (left + tipWidth > chartWidth) left = px - tipWidth - gap;
  return Math.max(0, left);
}
